import React, { useEffect, useState, useCallback } from "react";
import {
  Tabs, Table, Card, Tag, Button, Typography, Spin, Alert, Row, Col, Input,
  Pagination, Grid, message, Empty,
} from "antd";
import {
  CheckCircleTwoTone, CloseCircleTwoTone, EyeOutlined, SearchOutlined, FileSearchOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";
import { usePermissions } from "../../../../hooks/usePermissions";
import { ActionType, ModuleType, PermissionButton } from "../../../../components";
import ApprovalActionModal from "../../components/ApprovalActionModal";
import {
  fetchEntityTypes, fetchApprovalInstance, progressApprovalInstance, markApprovalViewed,
} from "../../api";
import { resolveColumnValue, resolveViewTarget } from "../../utils/requestDisplay";

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;
const PAGE_SIZE = 10;

// Fixed system tabs that always exist, in addition to the dynamic entity-type tabs.
const SYSTEM_TABS = {
  ALL: { key: "ALL", label: "All", status: "pending" },
  COMPLETED: { key: "COMPLETED", label: "Completed", status: "completed" },
  REJECTED: { key: "REJECTED", label: "Rejected", status: "rejected" },
};

const STATUS_TAG = {
  pending: { color: "blue", label: "PENDING" },
  completed: { color: "green", label: "APPROVED" },
  approved: { color: "green", label: "APPROVED" },
  rejected: { color: "red", label: "REJECTED" },
  escalated: { color: "orange", label: "ESCALATED" },
};

// resolveColumnValue lives in ../../utils/requestDisplay so this table and the
// approval detail page read an instance the same way.

const MyApprovals = () => {
  const { user } = useAuth();
  const { canViewModule, can } = usePermissions();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const userId = user?.id || user?._id;
  const [messageApi, contextHolder] = message.useMessage();

  // ?tab=<key> makes every tab addressable, so dashboard widgets and notifications
  // can deep-link straight to the view they describe (e.g. ?tab=COMPLETED).
  const [searchParams, setSearchParams] = useSearchParams();

  const [entityTypes, setEntityTypes] = useState([]);
  const [activeTab, setActiveTab] = useState(() => searchParams.get("tab") || "ALL");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: PAGE_SIZE });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [modal, setModal] = useState({ visible: false, actionType: null, instance: null, comment: "", loading: false });

  // ── Load registered entity types → dynamic tabs (no hardcoded types) ──
  useEffect(() => {
    fetchEntityTypes()
      .then((types) => setEntityTypes(Array.isArray(types) ? types : []))
      .catch(() => setEntityTypes([])); // degrade to system tabs only
  }, []);

  const currentTabDef = useCallback(() => {
    if (SYSTEM_TABS[activeTab]) return SYSTEM_TABS[activeTab];
    const et = entityTypes.find((e) => e.entityType === activeTab);
    return et ? { key: et.entityType, label: et.tab || et.displayName, status: "pending", entityType: et.entityType, meta: et } : SYSTEM_TABS.ALL;
  }, [activeTab, entityTypes]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    const def = currentTabDef();
    try {
      const params = { userId, status: def.status, page, limit: PAGE_SIZE, search };
      if (def.entityType) params.entityType = def.entityType;
      const res = await fetchApprovalInstance(params);
      setRows(res.data || []);
      setMeta(res.meta || { total: 0, page: 1, limit: PAGE_SIZE });
    } catch (e) {
      setError("Failed to load approvals");
    }
    setLoading(false);
  }, [userId, currentTabDef, page, search]);

  useEffect(() => { load(); }, [load]);

  // Follow the URL when it changes under us (back/forward, or a fresh deep link).
  useEffect(() => {
    const fromUrl = searchParams.get("tab") || "ALL";
    setActiveTab((cur) => (cur === fromUrl ? cur : fromUrl));
  }, [searchParams]);

  const changeTab = (key) => {
    setActiveTab(key);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (key === "ALL") next.delete("tab"); else next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  const isMyPendingStep = (record) => {
    const step = record.steps?.find((s) => s.stepNumber === record.currentStep);
    return String(record.status).toLowerCase() === "pending"
      && step && String(step.status).toLowerCase() === "pending"
      && Array.isArray(step.approvers) && step.approvers.some((a) => String(a) === String(userId));
  };

  const openAction = (instance, actionType) => setModal({ visible: true, actionType, instance, comment: "", loading: false });

  // `payload` is the decision-time input the modal collected (bank UTRs for a
  // payment approval); undefined for every request that needs none.
  const submitAction = async (payload) => {
    if (!modal.comment.trim()) { messageApi.warning("Comment is required"); return; }
    setModal((m) => ({ ...m, loading: true }));
    try {
      await progressApprovalInstance({ instanceId: modal.instance._id, action: modal.actionType, comment: modal.comment, payload });
      setModal({ visible: false, actionType: null, instance: null, comment: "", loading: false });
      messageApi.success(`Approval ${modal.actionType}`);
      load();
    } catch (err) {
      setModal((m) => ({ ...m, loading: false }));
      messageApi.error(err?.response?.data?.message || "Action failed");
    }
  };

  // The full approval — request, the record behind it, workflow, history — on its
  // own page. A side drawer could never hold what an approver actually needs to
  // read (a ten-line journal entry, the ledger entry a delete would remove), so
  // Details is a route, not an overlay.
  const openDetails = async (record) => {
    try { await markApprovalViewed(record._id); } catch { /* non-fatal */ }
    navigate(`/approvals/instance/${record._id}`);
  };

  // View → open the business module in context (per registry viewIntent);
  // anything unmapped falls back to the approval detail page.
  const openView = async (record) => {
    try { await markApprovalViewed(record._id); } catch { /* non-fatal */ }
    const et = entityTypes.find((e) => e.entityType === (record.entityType || record.module));
    const target = resolveViewTarget(record, et);
    navigate(target ? target.path : `/approvals/instance/${record._id}`);
  };


  // Build columns from registry metadata for entity tabs; generic for system tabs.
  const buildColumns = () => {
    const def = currentTabDef();
    let cols;
    if (def.meta && Array.isArray(def.meta.columns) && def.meta.columns.length) {
      cols = def.meta.columns.map((c) => ({
        title: c.label,
        key: c.key,
        render: (_, record) => {
          const val = resolveColumnValue(c.key, record);
          if (c.type === "badge" && c.key === "status") {
            const t = STATUS_TAG[String(val).toLowerCase()] || { color: "default", label: String(val).toUpperCase() };
            return <Tag color={t.color}>{t.label}</Tag>;
          }
          if (c.type === "currency" && val !== "-") return `₹${Number(val).toLocaleString("en-IN")}`;
          // Make the Case Number (businessRef) column a clickable link.
          if (c.key === "businessRef" && record.requestId?.displayMeta?.caseId) {
            return (
              <a
                style={{ fontWeight: 600 }}
                onClick={async (e) => {
                  e.stopPropagation();
                  try { await markApprovalViewed(record._id); } catch { /* non-fatal */ }
                  navigate(`/pledge-release-cases/${record.requestId.displayMeta.caseId}?tab=documents`);
                }}
              >
                {String(val)}
              </a>
            );
          }
          return String(val);
        },
      }));
    } else {

      cols = [
        { title: "Type", key: "entityType", render: (_, r) => <Tag>{r.entityType || r.module}</Tag> },
        { title: "Reference", key: "businessRef", render: (_, r) => resolveColumnValue("businessRef", r) },
        { title: "Requested By", key: "requestedBy", render: (_, r) => resolveColumnValue("requestedBy", r) },
        { title: "Requested On", key: "createdAt", render: (_, r) => resolveColumnValue("createdAt", r) },
        { title: "Current Step", key: "currentStep", render: (_, r) => resolveColumnValue("currentStep", r) },
        {
          title: "Status", key: "status",
          render: (_, r) => {
            const t = STATUS_TAG[String(r.status).toLowerCase()] || { color: "default", label: String(r.status).toUpperCase() };
            return <Tag color={t.color}>{t.label}</Tag>;
          },
        },
      ];
    }

    // Always append the actions column.
    cols.push({
      title: "Actions",
      key: "actions",
      fixed: screens.md ? "right" : undefined,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openView(record)}>View</Button>
          {/* View jumps to the business screen; Details opens the approval's own
              page — what is being approved, in full, plus the workflow trail. */}
          <Button
            size="small" icon={<FileSearchOutlined />}
            onClick={() => openDetails(record)}
          >Details</Button>
          {isMyPendingStep(record) && (
            <>
              <PermissionButton
                buttonType={ActionType.APPROVE} module={ModuleType.WORKFLOW}
                type="primary" size="small" onClick={() => openAction(record, "approved")}
                icon={<CheckCircleTwoTone twoToneColor="#fff" />}
              >Approve</PermissionButton>
              <PermissionButton
                buttonType={ActionType.REJECT} module={ModuleType.WORKFLOW}
                danger size="small" onClick={() => openAction(record, "rejected")}
                icon={<CloseCircleTwoTone twoToneColor="#fff" />}
              >Reject</PermissionButton>
            </>
          )}
        </div>
      ),
    });
    return cols;
  };

  if (!canViewModule(ModuleType.WORKFLOW) && !canViewModule(ModuleType.APPROVALS) && !can('workflow', 'myApprovals')) {
    return (
      <div style={{ marginTop: 32, textAlign: "center" }}>
        <Text type="danger">You do not have permission to view approvals.</Text>
      </div>
    );
  }

  // Tabs: All → each entity type (dynamic) → Completed → Rejected.
  const tabItems = [
    { key: SYSTEM_TABS.ALL.key, label: SYSTEM_TABS.ALL.label },
    ...entityTypes.map((e) => ({ key: e.entityType, label: e.tab || e.displayName || e.entityType })),
    { key: SYSTEM_TABS.COMPLETED.key, label: SYSTEM_TABS.COMPLETED.label },
    { key: SYSTEM_TABS.REJECTED.key, label: SYSTEM_TABS.REJECTED.label },
  ];

  return (
    <div className="container-fluid" style={{ marginTop: 24 }}>
      {contextHolder}
      <Card>
        <Row justify="space-between" align="middle" className="mb-3">
          <Col><Title level={3} style={{ margin: 0 }}>My Approvals</Title></Col>
          <Col xs={24} md={8}>
            <Input.Search
              placeholder="Search by reference / step"
              allowClear value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              onSearch={(v) => { setSearch(v); setPage(1); }}
              enterButton={<SearchOutlined />}
            />
          </Col>
        </Row>

        <Tabs
          activeKey={activeTab}
          onChange={changeTab}
          items={tabItems}
        />

        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
        <Spin spinning={loading}>
          <Table
            columns={buildColumns()}
            dataSource={rows}
            rowKey="_id"
            pagination={false}
            scroll={{ x: "max-content" }}
            size={screens.lg ? "middle" : "small"}
            locale={{ emptyText: <Empty description="No approvals in this view" /> }}
          />
          <Pagination
            style={{ marginTop: 16, textAlign: "right" }}
            current={meta.page} pageSize={meta.limit} total={meta.total}
            onChange={(p) => setPage(p)} showSizeChanger={false}
          />
        </Spin>
      </Card>

      <ApprovalActionModal
        visible={modal.visible}
        actionType={modal.actionType}
        comment={modal.comment}
        onChange={(e) => setModal((m) => ({ ...m, comment: e.target.value }))}
        onOk={submitAction}
        onCancel={() => setModal({ visible: false, actionType: null, instance: null, comment: "", loading: false })}
        loading={modal.loading}
        instanceId={modal.instance?._id}
        title={modal.actionType === "approved" ? "Approve this request?" : "Reject this request?"}
        okText={modal.actionType === "approved" ? "Approve" : "Reject"}
        cancelText="Cancel"
        description="A comment is required."
      />

    </div>
  );
};

export default MyApprovals;
